import React from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/pro-light-svg-icons/faUser';
import type { OnboardingIntent } from 'fym-shared/src/music24/onboardingIntent';
import { TypedMemo } from '../../common/types';
import { welcomeMessages as messages } from './welcomeMessages';
import { withBrandWord } from '../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole welcome page; each part uses its own slice of it */
import styles from './WelcomeView.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface IntentCopy {
  headline: MessageDescriptor;
  lead: MessageDescriptor;
}

/** design: onboarding a1 (no intent) and a2-a5 (the headline by intent). */
function intentCopy(intent: OnboardingIntent | null): IntentCopy {
  switch (intent) {
    case 'promote':
      return { headline: messages.headlinePromote, lead: messages.leadPromote };
    case 'analytics':
      return { headline: messages.headlineAnalytics, lead: messages.leadAnalytics };
    case 'publicData':
      return { headline: messages.headlinePublicData, lead: messages.leadPublicData };
    case 'teams':
      return { headline: messages.headlineTeams, lead: messages.leadDefault };
    case null:
      return { headline: messages.headlineDefault, lead: messages.leadDefault };
  }
}

// formatMessage returns an array when a message has tags, so the span has a key.
const renderGradient = (chunks: React.ReactNode): React.ReactNode => (
  <span key="grad" className={styles.gradText}>
    {chunks}
  </span>
);

interface WelcomeHeadlineProps {
  intent: OnboardingIntent | null;
}

/** The eyebrow, the headline and the lead of the welcome page (design: onboarding a1-a5). */
const WelcomeHeadlineComponent: React.FC<WelcomeHeadlineProps> = ({ intent }) => {
  const intl = useIntl();
  const copy = intentCopy(intent);
  return (
    <div className={styles.head}>
      <span className={styles.eyebrow}>
        <span className={styles.dot} />
        {withBrandWord(intl.formatMessage(messages.eyebrow))}
      </span>
      <h1 className={styles.headline}>{intl.formatMessage(copy.headline, { grad: renderGradient })}</h1>
      <p className={styles.lead}>{withBrandWord(intl.formatMessage(copy.lead))}</p>
      {/* design: onboarding a5. The teams intent adds one line about the team plans. */}
      {intent === 'teams' ? (
        <div className={styles.info}>
          <FontAwesomeIcon icon={faUser} />
          <span>{intl.formatMessage(messages.teamsNote)}</span>
        </div>
      ) : null}
    </div>
  );
};

export const WelcomeHeadline = TypedMemo(WelcomeHeadlineComponent);
